import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissions } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { formatClientNum } from '@/lib/format'
import {
  Search, X, Loader2, Users, Megaphone, LayoutDashboard,
  Command, Settings, Phone,
} from 'lucide-react'
import { CLIENT_STATUS_LABELS, CLIENT_STATUS_COLORS } from '@/types/core'
import type { ClientStatus } from '@/types/core'

const NAV_SHORTCUTS = [
  { label: 'Dashboard',     path: '/',              icon: LayoutDashboard },
  { label: 'Clientes',      path: '/clientes',      icon: Users },
  { label: 'Campañas',      path: '/campanas',      icon: Megaphone },
  { label: 'Cobros',        path: '/cobros',        icon: Phone },
  { label: 'Configuración', path: '/configuracion', icon: Settings },
]

interface ClientResult {
  id: string
  numero_cliente: number
  nombre: string | null
  cedula: string | null
  current_status: ClientStatus | null
}

function sanitizeTerm(raw: string): string {
  return raw.trim().replace(/[%_,.()]/g, ' ').replace(/\s+/g, ' ').trim()
}

export function GlobalSearch() {
  const [open, setOpen]           = useState(false)
  const [query, setQuery]         = useState('')
  const [results, setResults]     = useState<ClientResult[]>([])
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef  = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const userId = useAuthStore(s => s.user?.id)
  const { isAdmin, isSupervisor } = usePermissions()
  const isAdminOrSup = isAdmin || isSupervisor

  // ⌘K / Ctrl+K → enfoca el input del topbar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
        window.setTimeout(() => inputRef.current?.focus(), 20)
      }
      if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Búsqueda con debounce
  useEffect(() => {
    const term = sanitizeTerm(query)
    if (term.length < 1) {
      setResults([])
      setError(null)
      return
    }

    const timer = window.setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        let q = supabase
          .from('clients')
          .select('id, numero_cliente, nombre, cedula, current_status')
          .limit(10)

        if (!isAdminOrSup && userId) {
          q = q.eq('assigned_to', userId)
        }

        const asNum = Number(term.replace(/\D/g, ''))
        const isPureNum = /^\d+$/.test(term)

        if (isPureNum && !Number.isNaN(asNum)) {
          q = q.or(`numero_cliente.eq.${asNum},nombre.ilike.%${term}%,cedula.ilike.%${term}%`)
        } else {
          q = q.or(`nombre.ilike.%${term}%,cedula.ilike.%${term}%`)
        }

        q = q.order('numero_cliente', { ascending: true })

        const { data, error: qErr } = await q
        if (qErr) throw qErr
        setResults((data ?? []) as ClientResult[])
        setHighlight(0)
      } catch (e) {
        console.error('[GlobalSearch]', e)
        setResults([])
        setError('No se pudo buscar')
      } finally {
        setLoading(false)
      }
    }, 220)

    return () => window.clearTimeout(timer)
  }, [query, isAdminOrSup, userId])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setResults([])
    setError(null)
  }, [])

  const goTo = useCallback((path: string) => {
    navigate(path)
    close()
  }, [navigate, close])

  const openClient = useCallback((clientId: string) => {
    navigate('/clientes', { state: { openClientId: clientId } })
    close()
  }, [navigate, close])

  const flatItems = query.trim()
    ? results.map(r => ({ type: 'client' as const, id: r.id, data: r }))
    : NAV_SHORTCUTS.map(s => ({ type: 'nav' as const, id: s.path, data: s }))

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(h + 1, Math.max(0, flatItems.length - 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = flatItems[highlight]
      if (!item) return
      if (item.type === 'nav') goTo(item.data.path)
      else openClient(item.data.id)
    } else if (e.key === 'Escape') {
      close()
      inputRef.current?.blur()
    }
  }

  return (
    <div ref={wrapRef} className="relative shrink-0">
      {/* Barra de búsqueda siempre en el topbar */}
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border transition-colors',
          'bg-[var(--input-bg)]',
          open
            ? 'border-brand-500/50 ring-2 ring-brand-500/15'
            : 'border-[var(--border)] hover:border-[var(--border-hover)]',
          // Desktop: barra ancha; móvil: compacta al expandir
          'h-9 px-2.5',
          'w-9 md:w-56 lg:w-64',
          open && 'w-[min(100vw-6rem,20rem)] md:w-64 lg:w-72',
        )}
      >
        <Search size={14} strokeWidth={1.8} className="text-[var(--text-muted)] shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Buscar cliente o módulo…"
          className={cn(
            'flex-1 min-w-0 bg-transparent text-xs text-[var(--text-primary)]',
            'placeholder:text-[var(--text-muted)] outline-none',
            // En móvil colapsado el input se oculta visualmente hasta abrir
            'w-0 md:w-auto',
            open && 'w-auto',
          )}
          autoComplete="off"
          spellCheck={false}
          aria-label="Buscar"
          aria-expanded={open}
          aria-controls="global-search-results"
        />
        {loading && <Loader2 size={12} className="animate-spin text-brand-400 shrink-0" />}
        {open && query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus() }}
            className="shrink-0 p-0.5 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label="Limpiar"
          >
            <X size={12} />
          </button>
        )}
        <kbd className="hidden lg:flex text-[9px] bg-[var(--kbd-bg)] border border-[var(--border)] rounded px-1 py-0.5 font-mono items-center gap-0.5 shrink-0 text-[var(--text-muted)]">
          <Command size={8} /> K
        </kbd>
      </div>

      {/* Dropdown anclado debajo de la barra — NO modal */}
      {open && (
        <div
          id="global-search-results"
          role="listbox"
          className={cn(
            'absolute top-full left-0 mt-1.5 z-50',
            'w-[min(100vw-1.5rem,22rem)] md:w-72 lg:w-80',
            'rounded-xl border border-[var(--border)] bg-[var(--bg-surface)]',
            'shadow-xl overflow-hidden',
            // En móvil anclar al borde izquierdo de la pantalla si hace falta
            'max-md:fixed max-md:left-3 max-md:right-3 max-md:w-auto max-md:top-[52px]',
          )}
        >
          <div className="max-h-[min(60vh,380px)] overflow-y-auto overscroll-contain">
            {!query.trim() && (
              <div className="py-1.5 px-1.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)] px-2.5 py-1">
                  Navegación rápida
                </p>
                {NAV_SHORTCUTS.map((s, i) => {
                  const Icon = s.icon
                  return (
                    <button
                      key={s.path}
                      type="button"
                      role="option"
                      aria-selected={highlight === i}
                      onClick={() => goTo(s.path)}
                      onMouseEnter={() => setHighlight(i)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                        highlight === i ? 'bg-[var(--nav-hover-bg)]' : 'hover:bg-[var(--nav-hover-bg)]',
                      )}
                    >
                      <Icon size={14} strokeWidth={1.8} className="text-brand-400 shrink-0" />
                      <span className="text-[13px] font-medium text-[var(--text-primary)]">{s.label}</span>
                    </button>
                  )
                })}
              </div>
            )}

            {error && (
              <p className="px-4 py-6 text-center text-xs text-danger-500">{error}</p>
            )}

            {results.length > 0 && (
              <div className="py-1.5 px-1.5 border-t border-[var(--border)]">
                <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)] px-2.5 py-1">
                  Clientes ({results.length})
                </p>
                {results.map((c, i) => {
                  const st = (c.current_status ?? 'pendiente') as ClientStatus
                  const cfg = CLIENT_STATUS_COLORS[st] ?? CLIENT_STATUS_COLORS.pendiente
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="option"
                      aria-selected={highlight === i}
                      onClick={() => openClient(c.id)}
                      onMouseEnter={() => setHighlight(i)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                        highlight === i ? 'bg-[var(--nav-hover-bg)]' : 'hover:bg-[var(--nav-hover-bg)]',
                      )}
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-500/10 text-brand-400 text-[11px] font-bold">
                        {(c.nombre?.[0] ?? '?').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
                          {c.nombre ?? 'Sin nombre'}
                        </p>
                        <p className="text-[10px] text-[var(--text-muted)] font-mono truncate">
                          {formatClientNum(c.numero_cliente)}
                          {c.cedula ? ` · ${c.cedula}` : ''}
                        </p>
                      </div>
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
                        style={{ color: cfg.text, background: cfg.bg }}
                      >
                        {CLIENT_STATUS_LABELS[st] ?? st}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}

            {query.trim().length >= 1 && !loading && !error && results.length === 0 && (
              <div className="px-4 py-8 text-center text-[var(--text-muted)]">
                <Search size={18} strokeWidth={1.3} className="opacity-30 mx-auto mb-2" />
                <p className="text-xs">Sin resultados para "{query}"</p>
              </div>
            )}
          </div>

          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border-t border-[var(--border)] bg-[var(--bg-surface-2)]/40">
            <span className="text-[9px] text-[var(--text-muted)]">↑↓</span>
            <span className="text-[9px] text-[var(--text-muted)]">Enter</span>
            <span className="text-[9px] text-[var(--text-muted)]">Esc</span>
          </div>
        </div>
      )}
    </div>
  )
}
